import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { AvatarComponent } from "../../../../@shared/components/user/avatar/avatar.component";

// Modèles
import { ClientContact } from 'src/app/@shared/models/client/contact.model';
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';
import { PartnerContact } from 'src/app/@shared/models/partner/contact.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';
import { Sorter } from 'src/app/@shared/utilities/models/sorter';
import { Sorting } from 'src/app/@shared/utilities/array/sorting';

type ConversationContact = {
	contact: ClientContact | PartnerContact;
	isInternal: boolean;
	selected: boolean;
	id: string
}

@Component({
	selector: 'app-conversation-form',
	imports: [
		CommonModule,
		FormsModule,
		ButtonModule,
		CheckboxModule,
		ProgressSpinnerModule,
		AvatarComponent,
	],
	templateUrl: './conversation-form.component.html',
	styleUrl: './conversation-form.component.scss'
})
export class ConversationFormComponent extends FormBaseComponent {

	/*----------------------------------------------------------------------------------------
	 * Gestion de la vue
	 ----------------------------------------------------------------------------------------*/

	@Output()
	public cancel: EventEmitter<void> = new EventEmitter<void>();
	@Output()
	public saved: EventEmitter<Conversation> = new EventEmitter<Conversation>();

	private _loadingClientContacts: boolean = true;
	private _loadingPartnerContacts: boolean = true;

	/*----------------------------------------------------------------------------------------
	 * Données
	 ----------------------------------------------------------------------------------------*/

	private _availableContacts: ConversationContact[] = [];
	public get availableContacts(): ConversationContact[] {
		return this._availableContacts;
	}
	public set availableContacts(value: ConversationContact[]) {
		let emptyArray: ConversationContact[] = [];
		let sorter1: Sorter = new Sorter({
			property: 'contact.firstName',
		});
		let sorter2: Sorter = new Sorter({
			property: 'contact.lastName',
		});
		value = Sorting.sort(value, [sorter1, sorter2]);
		this._availableContacts = emptyArray.concat(value);
	}

	private _conversation!: Conversation;
	public get conversation(): Conversation {
		return this._conversation;
	}
	@Input()
	public set conversation(value: Conversation) {
		this._conversation = value;
	}

	/*----------------------------------------------------------------------------------------
	 * Accesseurs
	 ----------------------------------------------------------------------------------------*/

	public get createDisabled(): boolean {
		return this._availableContacts.filter((contact: ConversationContact) => contact.selected).length === 0;
	}

	public get loadingContacts(): boolean {
		return this._loadingClientContacts || this._loadingPartnerContacts;
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes publiques
	 ----------------------------------------------------------------------------------------*/

	public onClickCancel(): void {
		this.cancel.emit();
	}

	public onClickContact(contact: ConversationContact): void {
		contact.selected = !contact.selected;
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes protégées
	 ----------------------------------------------------------------------------------------*/

	protected override loadData(): void {
		this._loadClientContacts();
		this._loadPartnerContacts();
	}

	protected override save(): void {
		this._mapFromForm();
		this.conversationService.save$(this.conversation)
			.subscribe((conversation: Conversation) => {
				this.saved.emit(conversation);
			});
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes privées
	 ----------------------------------------------------------------------------------------*/

	private _loadClientContacts(): void {
		let filter: Filter = new Filter({
			property: 'user.id',
			comparator: Filter.COMPARATOR.NE,
			value: this.appService.user!.id
		});
		this.clientContactService.getList$({
			filters: [filter],
		}).subscribe((clientContacts: ClientContact[]) => {
			// Transformer en ConversationContact
			const internalContacts: ConversationContact[] = clientContacts.map(contact => ({
				contact,
				isInternal: true,
				selected: false,
				id: contact.id + '_int'
			}));
			// Ajouter à la liste unifiée
			this.availableContacts = [...this._availableContacts, ...internalContacts];
			this._loadingClientContacts = false;
		});
	}

	private _loadPartnerContacts(): void {
		// Il faut dans un premier temps charger l'objet lié à la conversation, c'est lui qui porte le partenaire
		if (this.conversation.objectId && this.conversation.entityType) {
			this.getServiceForEntityType(this.conversation.entityType).get$(this.conversation.objectId)
				.subscribe((object: any) => {
					if (object && object.partner) {
						let filter: Filter = new Filter({
							property: 'partner.id',
							comparator: Filter.COMPARATOR.EQ,
							value: object.partner.id
						});
						this.partnerContactService.getList$({
							filters: [filter],
						})
							.subscribe((partnerContacts: PartnerContact[]) => {
								// Transformer en ConversationContact
								const externalContacts: ConversationContact[] = partnerContacts.map(contact => ({
									contact,
									isInternal: false,
									selected: false,
									id: contact.id + '_ext'
								}));
								// Ajouter à la liste unifiée
								this.availableContacts = [...this._availableContacts, ...externalContacts];
								this._loadingPartnerContacts = false;
							});
					} else {
						this._loadingPartnerContacts = false;
					}
				});
		} else {
			this._loadingPartnerContacts = false;
		}
	}

	private _mapFromForm(): void {
		const selectedInternalContacts = this.availableContacts
			.filter(item => item.isInternal && item.selected)
			.map(item => item.contact as ClientContact);

		const selectedExternalContacts = this.availableContacts
			.filter(item => !item.isInternal && item.selected)
			.map(item => item.contact as PartnerContact);

		this.conversation.internalParticipants = selectedInternalContacts;
		this.conversation.externalParticipants = selectedExternalContacts;
	}

}
